import { useEffect, useMemo, useState } from 'react';
import type {
  RoutingSmartSettingsPatch,
  RoutingSmartSettingsResponse,
} from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button, FormField, Input } from '../../ui/index.ts';

interface Draft {
  readonly exploration: string;
  readonly refirePct: string;
  readonly retireDays: string;
  readonly retireBelow: string;
  readonly stability: string;
  readonly economy: string;
  readonly speed: string;
}

type DraftKey = keyof Draft;

function draftFromSettings(settings: RoutingSmartSettingsResponse): Draft {
  return {
    exploration: String(settings.exploration_budget_usd_per_day),
    refirePct: String(Math.round(settings.refire_budget_pct * 1000) / 10),
    retireDays: String(settings.retire_after_days),
    retireBelow: String(settings.retire_below),
    stability: String(settings.stability),
    economy: String(settings.economy),
    speed: String(settings.speed),
  };
}

function parseOptionalNumber(
  raw: string,
  check: (value: number) => boolean,
): { ok: true; value: number | null } | { ok: false } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return { ok: true, value: null };
  const value = Number(trimmed);
  if (!Number.isFinite(value) || !check(value)) return { ok: false };
  return { ok: true, value };
}

/**
 * SKU-level smart budgets. Empty fields PATCH `null` (server default).
 * Non-empty: exploration ≥ 0, refire 0–100%, days integer ≥ 1, threshold 0–1.
 */
export function TenantRoutingSmartSettingsBar({
  skuId,
  settings,
  saving,
  onSave,
}: {
  readonly skuId: string;
  readonly settings: RoutingSmartSettingsResponse;
  readonly saving: boolean;
  readonly onSave: (patch: RoutingSmartSettingsPatch) => void;
}): JSX.Element {
  const t = useT();
  const saved = useMemo(() => draftFromSettings(settings), [settings]);
  const [draft, setDraft] = useState<Draft>(saved);
  const [errors, setErrors] = useState<Partial<Record<DraftKey, true>>>({});

  useEffect(() => {
    setDraft(saved);
    setErrors({});
  }, [saved]);

  const dirty =
    draft.exploration !== saved.exploration ||
    draft.refirePct !== saved.refirePct ||
    draft.retireDays !== saved.retireDays ||
    draft.retireBelow !== saved.retireBelow ||
    draft.stability !== saved.stability ||
    draft.economy !== saved.economy ||
    draft.speed !== saved.speed;

  function clearField(key: DraftKey): void {
    setDraft({ ...draft, [key]: '' });
    setErrors({ ...errors, [key]: undefined });
  }

  function handleSave(): void {
    const exploration = parseOptionalNumber(draft.exploration, (value) => value >= 0);
    const refire = parseOptionalNumber(draft.refirePct, (value) => value >= 0 && value <= 100);
    const daysRaw = draft.retireDays.trim();
    const days = Number(daysRaw);
    const daysOk = daysRaw.length === 0 || (Number.isInteger(days) && days >= 1);
    const below = parseOptionalNumber(draft.retireBelow, (value) => value >= 0 && value <= 1);
    const parseSlider = (raw: string): { ok: true; value: number | null } | { ok: false } => {
      const trimmed = raw.trim();
      if (trimmed.length === 0) return { ok: true, value: null };
      const value = Number(trimmed);
      if (!Number.isInteger(value) || value < 0 || value > 100) return { ok: false };
      return { ok: true, value };
    };
    const stability = parseSlider(draft.stability);
    const economy = parseSlider(draft.economy);
    const speed = parseSlider(draft.speed);
    const nextErrors: Partial<Record<DraftKey, true>> = {
      ...(exploration.ok ? {} : { exploration: true }),
      ...(refire.ok ? {} : { refirePct: true }),
      ...(daysOk ? {} : { retireDays: true }),
      ...(below.ok ? {} : { retireBelow: true }),
      ...(stability.ok ? {} : { stability: true }),
      ...(economy.ok ? {} : { economy: true }),
      ...(speed.ok ? {} : { speed: true }),
    };
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (!exploration.ok || !refire.ok || !below.ok || !stability.ok || !economy.ok || !speed.ok) return;
    const patch: RoutingSmartSettingsPatch = {
      ...(draft.exploration !== saved.exploration
        ? { exploration_budget_usd_per_day: exploration.value }
        : {}),
      ...(draft.refirePct !== saved.refirePct
        ? { refire_budget_pct: refire.value === null ? null : refire.value / 100 }
        : {}),
      ...(draft.retireDays !== saved.retireDays
        ? { retire_after_days: daysRaw.length === 0 ? null : days }
        : {}),
      ...(draft.retireBelow !== saved.retireBelow ? { retire_below: below.value } : {}),
      ...(draft.stability !== saved.stability ? { stability: stability.value } : {}),
      ...(draft.economy !== saved.economy ? { economy: economy.value } : {}),
      ...(draft.speed !== saved.speed ? { speed: speed.value } : {}),
    };
    onSave(patch);
  }

  return (
    <div
      className="flex flex-col gap-3 border-t border-line-soft/50 px-4 py-3"
      data-testid={`tenant-routing-smart-settings-${skuId}`}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {([
          ['stability', 'tenant_routing.smart.stability', 'tenant_routing.smart.stability_hint'],
          ['economy', 'tenant_routing.smart.economy', 'tenant_routing.smart.economy_hint'],
          ['speed', 'tenant_routing.smart.speed', 'tenant_routing.smart.speed_hint'],
        ] as const).map(([key, labelKey, hintKey]) => (
          <FormField
            key={key}
            label={`${t(labelKey)} (${draft[key] === '' ? t('tenant_routing.smart.slider_default') : draft[key]})`}
            fieldId={`smart-${key}-${skuId}`}
            hint={t(hintKey)}
            error={errors[key] === true ? t('tenant_routing.smart.error.slider') : undefined}
          >
            <div className="flex items-center gap-2">
              <input
                id={`smart-${key}-${skuId}`}
                type="range"
                min={0}
                max={100}
                step={1}
                value={draft[key] === ''
                  ? (key === 'stability' ? 100 : key === 'economy' ? 70 : 40)
                  : Number(draft[key])}
                disabled={saving}
                onChange={(event) => setDraft({ ...draft, [key]: event.currentTarget.value })}
                className="h-2 w-full accent-signal"
                data-testid={`tenant-routing-smart-${key}-${skuId}`}
              />
              <Button
                size="sm"
                variant="ghost"
                disabled={saving}
                onClick={() => clearField(key)}
                data-testid={`tenant-routing-smart-${key}-clear-${skuId}`}
              >
                {t('tenant_routing.smart.restore_default')}
              </Button>
            </div>
          </FormField>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <FormField
          label={t('tenant_routing.smart.exploration_budget')}
          fieldId={`smart-exploration-${skuId}`}
          hint={t('tenant_routing.smart.exploration_budget_hint')}
          error={errors.exploration === true ? t('tenant_routing.smart.error.number') : undefined}
        >
          <div className="flex items-center gap-2">
            <Input
              id={`smart-exploration-${skuId}`}
              mono
              inputMode="decimal"
              value={draft.exploration}
              invalid={errors.exploration === true}
              disabled={saving}
              onChange={(event) => setDraft({ ...draft, exploration: event.currentTarget.value })}
              data-testid={`tenant-routing-smart-exploration-${skuId}`}
            />
            <Button
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => clearField('exploration')}
              data-testid={`tenant-routing-smart-exploration-clear-${skuId}`}
            >
              {t('tenant_routing.smart.restore_default')}
            </Button>
          </div>
        </FormField>
        <FormField
          label={t('tenant_routing.smart.refire_budget')}
          fieldId={`smart-refire-${skuId}`}
          hint={t('tenant_routing.smart.refire_budget_hint')}
          error={errors.refirePct === true ? t('tenant_routing.smart.error.percent') : undefined}
        >
          <div className="flex items-center gap-2">
            <Input
              id={`smart-refire-${skuId}`}
              mono
              inputMode="decimal"
              value={draft.refirePct}
              invalid={errors.refirePct === true}
              disabled={saving}
              onChange={(event) => setDraft({ ...draft, refirePct: event.currentTarget.value })}
              data-testid={`tenant-routing-smart-refire-${skuId}`}
            />
            <Button
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => clearField('refirePct')}
              data-testid={`tenant-routing-smart-refire-clear-${skuId}`}
            >
              {t('tenant_routing.smart.restore_default')}
            </Button>
          </div>
        </FormField>
        <FormField
          label={t('tenant_routing.smart.retire_after_days')}
          fieldId={`smart-retire-days-${skuId}`}
          hint={t('tenant_routing.smart.retire_after_days_hint')}
          error={errors.retireDays === true ? t('tenant_routing.smart.error.days') : undefined}
        >
          <div className="flex items-center gap-2">
            <Input
              id={`smart-retire-days-${skuId}`}
              mono
              inputMode="numeric"
              value={draft.retireDays}
              invalid={errors.retireDays === true}
              disabled={saving}
              onChange={(event) => setDraft({ ...draft, retireDays: event.currentTarget.value })}
              data-testid={`tenant-routing-smart-retire-days-${skuId}`}
            />
            <Button
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => clearField('retireDays')}
              data-testid={`tenant-routing-smart-retire-days-clear-${skuId}`}
            >
              {t('tenant_routing.smart.restore_default')}
            </Button>
          </div>
        </FormField>
        <FormField
          label={t('tenant_routing.smart.retire_below')}
          fieldId={`smart-retire-below-${skuId}`}
          hint={t('tenant_routing.smart.retire_below_hint')}
          error={errors.retireBelow === true ? t('tenant_routing.smart.error.threshold') : undefined}
        >
          <div className="flex items-center gap-2">
            <Input
              id={`smart-retire-below-${skuId}`}
              mono
              inputMode="decimal"
              value={draft.retireBelow}
              invalid={errors.retireBelow === true}
              disabled={saving}
              onChange={(event) => setDraft({ ...draft, retireBelow: event.currentTarget.value })}
              data-testid={`tenant-routing-smart-retire-below-${skuId}`}
            />
            <Button
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => clearField('retireBelow')}
              data-testid={`tenant-routing-smart-retire-below-clear-${skuId}`}
            >
              {t('tenant_routing.smart.restore_default')}
            </Button>
          </div>
        </FormField>
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={saving || !dirty}
          onClick={handleSave}
          data-testid={`tenant-routing-smart-settings-save-${skuId}`}
        >
          {t('tenant_routing.smart.save_settings')}
        </Button>
      </div>
    </div>
  );
}
